import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createPublicDataClient, signPublicCover, signPublicCoverVariants } from "@/integrations/fc/public-data.server";
import { PUBLIC_MEETING_LIMIT, PUBLIC_COVER_REFRESH_SECONDS } from "@/lib/security/content-policy";

export type Meeting = {
  id: string;
  slug: string;
  title: string;
  event_date: string | null;
  date_label: string | null;
  location: string | null;
  category: string;
  excerpt: string | null;
  body: string | null;
  cover_image_url: string | null;
  cover_image_srcset?: string | undefined;
  is_upcoming: boolean;
  members_only: boolean;
  hubspot_portal_id?: string | null;
  hubspot_form_id?: string | null;
  hubspot_region?: string | null;
};

// Public reads never select description fields; texts come only from the
// access-checked fetchMeetingDetail server function.
const PUBLIC_MEETING_FIELDS =
  "id, slug, title, event_date, date_label, location, category, cover_image_url, is_upcoming, members_only, hubspot_portal_id, hubspot_form_id, hubspot_region";

const text = (v: unknown) => (typeof v === "string" ? v : null);

/** Maps an own-backend public row to a Meeting with no description fields. */
export function toPublicMeeting(row: Record<string, unknown>): Meeting | null {
  const id = text(row["id"]), slug = text(row["slug"]), title = text(row["title"]);
  if (!id || !slug || !title || row["members_only"] === true) return null;
  return {
    id, slug, title,
    event_date: text(row["event_date"]),
    date_label: text(row["date_label"]),
    location: text(row["location"]),
    category: text(row["category"]) ?? "Meeting",
    excerpt: null,
    body: null,
    cover_image_url: text(row["cover_image_url"]),
    is_upcoming: row["is_upcoming"] === true,
    members_only: false,
    hubspot_portal_id: text(row["hubspot_portal_id"]),
    hubspot_form_id: text(row["hubspot_form_id"]),
    hubspot_region: text(row["hubspot_region"]),
  };
}

/** Upcoming events first, soonest first; then past events, newest first. */
export function sortMeetings<T extends Pick<Meeting, "is_upcoming" | "event_date">>(meetings: T[]): T[] {
  const time = (m: T) => (m.event_date ? Date.parse(m.event_date) : Number.NaN);
  const upcoming = meetings.filter((m) => m.is_upcoming)
    .sort((a, b) => (time(a) || Infinity) - (time(b) || Infinity));
  const past = meetings.filter((m) => !m.is_upcoming)
    .sort((a, b) => (time(b) || -Infinity) - (time(a) || -Infinity));
  return [...upcoming, ...past];
}

/** Reads the public list straight from the shared database (column grants + RLS). */
async function readMeetingsDirect(limit: number): Promise<unknown[] | null> {
  // Newest dates first, so upcoming events are always inside the limit.
  const { data, error } = await createPublicDataClient()
    .from("meetings")
    .select(PUBLIC_MEETING_FIELDS)
    .eq("status", "published")
    .eq("members_only", false)
    .order("event_date", { ascending: false })
    .limit(limit);
  if (error) { console.error("meeting list query failed", error.code); return null; }
  return (data ?? []) as unknown[];
}

const getMeetings = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ limit: z.number().int().positive().max(100).optional() }).parse(input))
  .handler(async ({ data }): Promise<Meeting[]> => {
  const started = performance.now();
  const limit = data.limit ?? PUBLIC_MEETING_LIMIT;
  const rows = (await readMeetingsDirect(Math.min(100, limit + 10))) ?? [];
  const readFinished = performance.now();
  const meetings = rows
    .map((r) => (r && typeof r === "object" ? toPublicMeeting(r as Record<string, unknown>) : null))
    .filter((m): m is Meeting => m !== null);
  const sorted = sortMeetings(meetings).slice(0, data.limit ?? PUBLIC_MEETING_LIMIT);
  const featured = sorted.find((meeting) => meeting.is_upcoming) ?? sorted[0];
  const latest = sorted.find((meeting) => !meeting.is_upcoming);
  const heroIds = new Set([featured?.id, latest?.id].filter((id): id is string => typeof id === "string"));
  const photoIds = new Set(sorted.filter((meeting) => !meeting.is_upcoming).map((meeting) => meeting.id));
  const covers = await signPublicCoverVariants(sorted, heroIds, photoIds);
  const { setResponseHeader } = await import("@tanstack/react-start/server");
  setResponseHeader("Server-Timing", `meetings;dur=${(readFinished - started).toFixed(1)}, covers;dur=${(performance.now() - readFinished).toFixed(1)}`);
  return sorted.map((meeting, index) => ({ ...meeting, cover_image_url: covers[index]?.src ?? null, cover_image_srcset: covers[index]?.srcSet }));
});

const getMeeting = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().trim().min(1).max(200) }).parse(input))
  .handler(async ({ data: input }): Promise<Meeting | null> => {
  const db = createPublicDataClient();
  const { data, error } = await db
    .from("meetings")
    .select(PUBLIC_MEETING_FIELDS)
    .eq("status", "published")
    .eq("members_only", false)
    .eq("slug", input.slug)
    .maybeSingle();

  if (error) { console.error("content query failed", error.code); throw new Error("Content could not be loaded."); }
  if (!data) return null;
  const meeting = { ...(data as unknown as Omit<Meeting, "excerpt" | "body">), excerpt: null, body: null } as Meeting;
  return { ...meeting, cover_image_url: await signPublicCover(meeting) };
});

export const meetingsQuery = (limit?: number) =>
  queryOptions({
    queryKey: ["meetings", limit ?? "all"],
    queryFn: () => getMeetings({ data: { limit } }),
    // Keep lazy/filtered photos usable on long visits without extending access.
    // Each refresh still independently revalidates visibility through RLS.
    staleTime: PUBLIC_COVER_REFRESH_SECONDS * 1000,
    refetchInterval: PUBLIC_COVER_REFRESH_SECONDS * 1000,
    refetchIntervalInBackground: false,
  });

export const meetingQuery = (slug: string) =>
  queryOptions({
    queryKey: ["meeting", slug],
    queryFn: () => getMeeting({ data: { slug } }),
    staleTime: 4 * 60 * 1000,
  });

